import { useEffect } from "react";
import isEmpty from 'lodash.isempty';

// Services & hooks
import { toggleLoading } from "../../services/helpers/index";
import { jobGetCode, jobFetchJob } from "../../services/job";
import { paymentService } from '../../services/payment/index';
import { injectIntl, defineMessages } from "react-intl";
import PropTypes from 'prop-types';
import usePayment from "../../hooks/usePayment";
import useSessionStorage from "../../hooks/useSessionStorage";

// Strings for translation in this view
const messages = defineMessages({
	paymentVerification: {
		id: "Vahvistetaan maksua",
		defaultMessage: "Vahvistetaan maksua"
	},
	paymentCancelled: {
		id: "Maksaminen keskeytetty",
		defaultMessage: "Maksaminen keskeytetty"
	},
	paymentException: {
		id: "Tapahtui virhe",
		defaultMessage: "Tapahtui virhe"
	},
	paymentSuccess: {
		id: "Kiitos! Maksusi on nyt kunnossa.",
		defaultMessage: "Kiitos! Maksusi on nyt kunnossa."
	},
	paymentSuccess151Or155: {
		id: "Kiitos maksustasi! Pyydämme energiayhtiötäsi peruuttamaan jakelun keskeytyksen. Jos energiayhtiösi on kuitenkin jo ehtinyt aloittaa katkaisuun johtavat toimet ja jakelusi keskeytyy, tulee kaikki avoimet erääntyneet laskut, mahdollinen vakuuslasku sekä katkaisu- ja kytkentälasku olla maksettuna ennen uudelleenkytkentää. Huomioithan, että Ropo ei tee energianjakelun keskeytyksiä, joten yhteydenottosi asiakaspalveluumme ei vaikuta mahdollisiin katkaisutoimiin.",
		defaultMessage: "Kiitos maksustasi! Pyydämme energiayhtiötäsi peruuttamaan jakelun keskeytyksen. Jos energiayhtiösi on kuitenkin jo ehtinyt aloittaa katkaisuun johtavat toimet ja jakelusi keskeytyy, tulee kaikki avoimet erääntyneet laskut, mahdollinen vakuuslasku sekä katkaisu- ja kytkentälasku olla maksettuna ennen uudelleenkytkentää. Huomioithan, että Ropo ei tee energianjakelun keskeytyksiä, joten yhteydenottosi asiakaspalveluumme ei vaikuta mahdollisiin katkaisutoimiin."
	},
	paymentSuccess156: {
		id: "Olemme vastaanottaneet maksuilmoituksesi ja käsittelemme sitä parhaillaan. Tämän jälkeen uudelleenkytkentä siirtyy energiayhtiösi käsittelyyn. Jos takaisinkytkennälle ilmenee esteitä, otamme sinuun välittömästi yhteyttä. Huomioithan, että yhteydenottosi asiakaspalveluumme ei nopeuta asiasi käsittelyä.",
		defaultMessage: "Olemme vastaanottaneet maksuilmoituksesi ja käsittelemme sitä parhaillaan. Tämän jälkeen uudelleenkytkentä siirtyy energiayhtiösi käsittelyyn. Jos takaisinkytkennälle ilmenee esteitä, otamme sinuun välittömästi yhteyttä. Huomioithan, että yhteydenottosi asiakaspalveluumme ei nopeuta asiasi käsittelyä."
	},
	authError: {
		id: 'Tunnistautuminen epäonnistui. Ole hyvä ja yritä uudelleen.',
		defaultMessage: 'Tunnistautuminen epäonnistui. Ole hyvä ja yritä uudelleen.'
	}
});

export { messages };

const Payment = (props) => {
	// translations
	const lng = props.intl.formatMessage;
	// hooks
	const { checkPaymentStatus } = usePayment();
	const { setSessionItem, getSessionItem } = useSessionStorage();

	/**
	 * Parse query parameters from props or window location. 
	 * Both options must be checked because router may cause url malformation when returning from some payment providers.
	 * 
	 * @param {*} props 
	 * @returns {array} - Parsed query parameters as key-value pairs.
	 */
	const parseQueryParams = (props) => {
		// use props location whenever possible, but check window location
		const location = isEmpty(props.location.search) ? window.location.search : props.location.search;

		// Throw error location search undefined or null
		if (isEmpty(location)) {
			throw new Error('Location search undefined or null');
		}

		// parse query parameters from location search
		const searchParams = new URLSearchParams(location);
		let args = [];

		for (const [key, value] of searchParams.entries()) {
			args[key] = value;
		}

		return args;
	}

	/**
	 * Handle payments from Worldline.
	 * Skips the payment status check from backend, because it is handled in the Worldline callback.
	 * @param {boolean} paymentSuccess
	 */
	const handleWorldlinePayments = (paymentSuccess) => {
		// form the alert message (success or error messages)
		const alert = paymentSuccess ? formAlertMessage() : formAlertMessage(true);
		paymentService.resetPaymentService();

		// replace with hash routing format to ensure no query parameters remain in the url, which is possible with router version 
		window.history.replaceState(null, '', `${window.location.origin}/#/job`);
		redirectToJob(alert);
	}

	/**
	 * Helper for forming the alert message content to pass to invoice page.
	 * The message is different, based on the job status before the payment was made.
	 *
	 * @returns {Object} - The alert message object.
	 */
	const formAlertMessage = (error) => {
		if (error) {
			return { message: lng(messages.paymentException) + '. ' + lng(messages.paymentCancelled), type: 'danger' };
		}

		// get job's status before it was paid
		const prevStatus = getSessionItem('previousJobStatus');

		// show success message based on previous status
		if (prevStatus?.toString() === '151' || prevStatus?.toString() === '155') {
			return { message: lng(messages.paymentSuccess151Or155), type: 'success' };
		} else if (prevStatus?.toString() === '156') {
			return { message: lng(messages.paymentSuccess156), type: 'success' };
		}

		return { message: lng(messages.paymentSuccess), type: 'success' };
	}

	/**
	 * Handle payment status check.
	 * Form the appropriate alert message and handle redirection to job view.
	 *
	 * @param {string} transactionId
	 * @param {string} serviceParam
	 */
	const handleStatusCheck = async (transactionId, serviceParam) => {
		const { error } = await checkPaymentStatus(transactionId, serviceParam);
		const alert = error ? formAlertMessage(error) : formAlertMessage();
		paymentService.resetPaymentService();
		redirectToJob(alert);
	};

	useEffect(() => {
		// Alert content
		let alert = {};

		try {
			const status = props.match?.params?.status ?? null;
			const provider = props.match?.params?.provider ?? null;

			// set payment provider to session storage when applicable to ensure callback parameters are checked correctly
			if (Object.values(paymentService.PAYMENTMETHODS).includes(provider)) {
				setSessionItem('paymentService', provider);
			}

			// parse args from props query string
			const args = parseQueryParams(props);
			const transactionId = paymentService.getCallbackTransactionId(args);
			const paymentSuccess = paymentService.getCallbackSuccess(args, status);

			// loading spinner and message
			if (paymentSuccess) {
				// Message to user that transaction is verifying
				toggleLoading(true, lng(messages.paymentVerification) + " (" + transactionId + ")...");
			} else {
				// Message to user that transaction is cancelled (or failed for some reason)
				toggleLoading(true, lng(messages.paymentCancelled) + "...");
			}

			/**
			 * If paymentservice is Worldline, redirect to job and skip the payment status call,
			 * because it is handled in its own callback.
			 */
			if (provider === paymentService.PAYMENTMETHODS.WORLDLINE) {
				handleWorldlinePayments(paymentSuccess);
				return;
			}

			const storageItem = getSessionItem('paymentService');
			let serviceParam = '';

			// check if service should be added to query parameters
			if (Object.values(paymentService.PAYMENTMETHODS).indexOf(storageItem) > -1) {
				serviceParam = '?service=' + storageItem;
			}

			// check payment status from backend
			handleStatusCheck(transactionId, serviceParam);

		} catch (error) {
			// Show generic error message
			alert = formAlertMessage(error);
			paymentService.resetPaymentService();
			redirectToJob(alert);
		}
	}, []);


	/**
	 * Reload job and redirect
	 */
	const redirectToJob = (alert) => {
		const dpid = jobGetCode();

		jobFetchJob(dpid)
			.then(function () {
				toggleLoading(false);

				props.history.replace({
					pathname: '/job',
					alert
				});
			})
			.catch(function (error) {
				toggleLoading(false);

				if (error.response?.status === 401) {
					const alert = { message: lng(messages.authError), type: 'danger' };
					props.history.push({
						pathname: "/logout",
						alert
					});
				} else {
					// If fetchjob fails -> cannot refresh job
					// go still back, there is job with status before payment
					alert = { message: lng(messages.paymentException) + '. ' + lng(messages.paymentCancelled), type: 'danger' };

					props.history.replace({
						pathname: '/job',
						alert
					});
				}
			});
	}

	return (<div></div>);
};

Payment.propTypes = {
	intl: PropTypes.object.isRequired,
	history: PropTypes.any.isRequired,
	location: PropTypes.any.isRequired,
	match: PropTypes.object,
};

export default injectIntl(Payment);